/**
 * notify — the app's transient-feedback API.
 *
 * Why this exists on top of `sonner`:
 *
 *   1. **Every status looks like its status.** Sonner's bare `toast()` renders
 *      an untyped pill, and roughly 50 call sites in this app were firing
 *      `toast.error(t("error.generic"))` — a red-less, iconless, actionless
 *      string that tells the user something broke and nothing else. A status
 *      here always carries an icon (never colour alone, per WCAG 1.4.1) and an
 *      error always gets reading time.
 *   2. **Errors name a recovery.** `notify.error` takes an `action`, so the
 *      call site that knows how to retry can offer it in the toast instead of
 *      leaving the user to re-find the control they just pressed.
 *   3. **One toast per subject.** Passing `id` collapses repeats — a stream
 *      that fails three times updates one toast instead of stacking three.
 *
 * i18n: every string here is caller-supplied and already translated. This
 * module never sees a key and never hardcodes user-facing copy, which is how
 * it stays usable from consumer, seller and admin surfaces alike.
 *
 * Icons are Lucide at the shared `ICON_STROKE` weight so the toast layer reads
 * as part of the same icon system as the rest of the product.
 */
import type { ReactElement, ReactNode } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  Info,
  LoaderCircle,
  OctagonAlert,
  Undo2,
} from "lucide-react";
import { toast, type ExternalToast } from "sonner";

import { ICON_STROKE } from "@/lib/icons";

/** Auto-dismiss windows. Errors get longer — they have to be read, not glimpsed. */
const DURATION = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 8000,
} as const;

export interface NotifyOptions {
  /** Second line: the detail, the constraint, or what happens next. */
  description?: ReactNode;
  /**
   * The recovery. On an error this is nearly always "Retry"; on a destructive
   * success it is "Undo". Omitting it on an error is a decision, not a default.
   */
  action?: { label: string; onClick: () => void };
  /** A quiet secondary control (e.g. "Not now"). */
  cancel?: { label: string; onClick?: () => void };
  /** Collapse repeats of the same subject onto one toast. */
  id?: string | number;
  /** Override the status default. `Infinity` keeps it up until dismissed. */
  duration?: number;
  /** Fired when the toast leaves for any reason. */
  onDismiss?: () => void;
}

function iconSlot(node: ReactElement): ReactElement {
  return node;
}

/** Maps the shared option shape onto Sonner's, dropping empty keys. */
function toSonner(
  options: NotifyOptions | undefined,
  fallbackDuration: number,
): ExternalToast {
  const out: ExternalToast = {
    duration: options?.duration ?? fallbackDuration,
  };
  if (options?.description !== undefined) out.description = options.description;
  if (options?.id !== undefined) out.id = options.id;
  if (options?.onDismiss) out.onDismiss = options.onDismiss;
  if (options?.action) {
    out.action = { label: options.action.label, onClick: options.action.onClick };
  }
  if (options?.cancel) {
    out.cancel = {
      label: options.cancel.label,
      onClick: options.cancel.onClick ?? (() => undefined),
    };
  }
  return out;
}

export const notify = {
  /** Something the user asked for completed. */
  success(title: ReactNode, options?: NotifyOptions) {
    return toast.success(title, {
      ...toSonner(options, DURATION.success),
      icon: iconSlot(<BadgeCheck size={17} strokeWidth={ICON_STROKE} />),
    });
  },

  /**
   * Something failed. Pass `action` whenever the caller can retry — an error
   * the user cannot act on is a dead end, and this app had ~50 of them.
   */
  error(title: ReactNode, options?: NotifyOptions) {
    return toast.error(title, {
      ...toSonner(options, DURATION.error),
      icon: iconSlot(<OctagonAlert size={17} strokeWidth={ICON_STROKE} />),
    });
  },

  /** It worked, but with a caveat the user needs to know about. */
  warning(title: ReactNode, options?: NotifyOptions) {
    return toast.warning(title, {
      ...toSonner(options, DURATION.warning),
      icon: iconSlot(<AlertTriangle size={17} strokeWidth={ICON_STROKE} />),
    });
  },

  /** Neutral, non-blocking news. */
  info(title: ReactNode, options?: NotifyOptions) {
    return toast.info(title, {
      ...toSonner(options, DURATION.info),
      icon: iconSlot(<Info size={17} strokeWidth={ICON_STROKE} />),
    });
  },

  /**
   * A destructive action that just happened and can be taken back. Held for
   * longer than a plain success because the whole point is the window to act.
   */
  undo(title: ReactNode, onUndo: () => void, undoLabel: string, options?: NotifyOptions) {
    return toast(title, {
      ...toSonner(options, 8000),
      action: { label: undoLabel, onClick: onUndo },
      icon: iconSlot(<Undo2 size={17} strokeWidth={ICON_STROKE} />),
    });
  },

  /**
   * A long-running operation, shown as one toast that transitions through its
   * own states rather than a spinner the user has to keep watching. `error`
   * receives the rejection so the message can name the actual failure.
   */
  promise<T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((value: T) => string);
      error: string | ((error: unknown) => string);
    },
  ) {
    return toast.promise(promise, {
      loading: messages.loading,
      success: messages.success,
      error: messages.error,
      duration: DURATION.success,
      icon: iconSlot(
        <LoaderCircle
          size={17}
          strokeWidth={ICON_STROKE}
          className="g-spin"
          aria-hidden="true"
        />,
      ),
    });
  },

  /** Dismiss one toast, or all of them when no id is given. */
  dismiss(id?: string | number) {
    toast.dismiss(id);
  },
};

export default notify;
